import { DataQualityTab } from './DataQualityTab';
import type { QualityReport } from '../../../shared/dataQuality';
import { invoiceKindOptions } from '../../components/InvoiceKind';
import { CsvExportButton, useCsvExport } from '../../components/CsvExport';
import { useViewState } from '../../lib/useViewState';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { melbourneToday } from '../../../shared/dates';
import { ViewChips, type SavedViewItem } from '../../components/ViewChips';
import { quarterOf } from '../../../shared/finance';
import { EXPENSE_CATEGORIES, INVOICE_KINDS, type InvoiceKind, type ClientWithCounts, type ExpenseWithNames, type FinanceReport, type InvoiceWithNames, type ProjectWithClient } from '../../../shared/types';
import { FilterBar, PageHeader, SearchBox, SkeletonRows, Tabs } from '../../components/primitives';
import { reportError } from '../../lib/errorEvents';
import { useIsMobile } from '../../lib/useIsMobile';
import { useQuickAddAction } from '../../lib/quickAdd';
import { DotsIcon, FilterIcon, SearchIcon, XIcon } from '../../components/icons';
import { PageMenu, type PageMenuItem } from '../../components/PageMenu';
import { PickerSheet } from '../../components/PickerSheet';
import { ReceivablesAging, agingExportUrl } from '../../components/ReceivablesAging';
import { isUnpaidReceivable, missingAgingDate } from '../../../shared/receivables';
import { FinanceFilterSheet } from '../../components/financeFilters';
import { financeFiltersDefaults, financeSavedParams, type FinanceFilters } from '../../lib/financeListMeta';
import type { FinanceSelection, Tab } from './types';
import { matchesQuery, financeViewCodec, capitalize, fyList } from './helpers';
import { setSelectedFY, useSelectedFY } from './hooks';
import { FYSwitcher } from './FYSwitcher';
import { InvoicesTab } from './InvoicesTab';
import { ExpensesTab } from './ExpensesTab';
import { ReportsTab } from './ReportsTab';
import { NewInvoiceModal } from './NewInvoiceModal';
import { ExpenseModal } from './ExpenseModal';

// Finance per mockups/06-finance.html (status-grouped invoices),
// 06-finance-expenses.html (month-grouped), 06-finance-reports.html (BAS
// card + AU-FY quarterly table + FY switcher + CSV export), and the
// 09-new-invoice / 09-new-expense modals. Invoice rows open the detail page
// (06-invoice-detail); expense rows reopen their modal for editing.

export default function Finance() {
  const { initial, publish } = useViewState(financeViewCodec, state => {
    setTab(state.tab);
    setQuery(state.query);
    setInvClient(state.invClient);
    setInvKind(state.invKind);
    setExpCategory(state.expCategory);
    setShowAllPaid(state.showAllPaid);
    setShowAllExpenses(state.showAllExpenses);
    setUnpaid(state.unpaid);
    setUndated(state.undated);
  });
  const [tab, setTab] = useState<Tab>(() => initial.tab);
  const [invoices, setInvoices] = useState<InvoiceWithNames[]>([]);
  const [expenses, setExpenses] = useState<ExpenseWithNames[]>([]);
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(null);
  const [qualityError, setQualityError] = useState<string | null>(null);
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [clients, setClients] = useState<ClientWithCounts[]>([]);
  const [projects, setProjects] = useState<ProjectWithClient[]>([]);
  const [query, setQuery] = useState(() => initial.query);
  const [invKind, setInvKind] = useState<InvoiceKind | ''>(() => initial.invKind);
  const [invClient, setInvClient] = useState<string | null>(() => initial.invClient);
  const [expCategory, setExpCategory] = useState<string | null>(() => initial.expCategory);
  const [showAllPaid, setShowAllPaid] = useState(() => initial.showAllPaid);
  const [showAllExpenses, setShowAllExpenses] = useState(() => initial.showAllExpenses);

  const [unpaid, setUnpaid] = useState(() => initial.unpaid);
  const [undated, setUndated] = useState(() => initial.undated);
  const [agingAsOf, setAgingAsOf] = useState(melbourneToday);
  const [agingReady, setAgingReady] = useState<string | null>(null);
  const filteredInvoices = invoices.filter(invoice =>
    (!(unpaid || undated) || isUnpaidReceivable(invoice)) && (!undated || missingAgingDate(invoice) !== null));

  useEffect(() => {
    publish({ tab, query, invKind, invClient, expCategory, showAllPaid, showAllExpenses, unpaid, undated });
  }, [publish, tab, query, invKind, invClient, expCategory, showAllPaid, showAllExpenses, unpaid, undated]);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [expenseModal, setExpenseModal] = useState<'new' | ExpenseWithNames | null>(null);
  // Quiet loading state (Phase 1.3) — skeleton until the first round of list
  // fetches settles (success or failure), instead of an empty-tab flash.
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const today = melbourneToday();
  const currentQuarter = quarterOf(today)!;
  const fyKey = currentQuarter.key.split(' ')[0]; // "FY26"

  // ── Mobile (§8.1): the toolbar becomes a chip row plus three icon buttons,
  // and the filter sheet edits a draft copy of exactly the saved-view state.
  const isMobile = useIsMobile();
  const [showMenu, setShowMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [draft, setDraft] = useState<FinanceFilters>(financeFiltersDefaults());
  const [savedViews, setSavedViews] = useState<SavedViewItem[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selection, setSelection] = useState<FinanceSelection | null>(null);
  const [fyPicker, setFyPicker] = useState(false);
  // Keep the chosen Finance chip visible on phones without moving the
  // page vertically. Reports also reveals its existing FY control at the end.
  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMobile) return;
    const row = tabsRef.current;
    if (!row) return;
    if (tab === 'reports') { row.scrollLeft = row.scrollWidth; return; }
    const active = row.querySelector<HTMLElement>('.t.on');
    if (!active) return;
    const bounds = row.getBoundingClientRect(), selected = active.getBoundingClientRect();
    if (selected.right > bounds.right - 20) row.scrollLeft += selected.right - bounds.right + 20;
    else if (selected.left < bounds.left + 20) row.scrollLeft -= bounds.left + 20 - selected.left;
  }, [isMobile, tab, report, qualityReport?.total_issues]);
  // The FAB's quick-add sheet leads with the row for whichever tab is open.
  useQuickAddAction(
    tab === 'invoices'
      ? { label: 'New invoice', onSelect: () => setShowNewInvoice(true) }
      : tab === 'expenses'
        ? { label: 'New expense', onSelect: () => setExpenseModal('new') }
        : null,
  );

  // Saved views (phase 24): exactly the filter/search/tab state this page
  // already holds — applying a view must round-trip identically.
  const viewState = { tab, query, ...(invKind ? { kind: invKind } : {}), client: invClient, category: expCategory, ...(unpaid ? { unpaid: '1' } : {}), ...(undated ? { undated: '1' } : {}) };
  const isDefaultView = tab === 'invoices' && !query && !invKind && !invClient && !expCategory && !unpaid && !undated;
  const applyView = (p: Record<string, string>) => {
    setTab(p.tab === 'expenses' || p.tab === 'reports' || p.tab === 'data-quality' ? p.tab : 'invoices');
    setQuery(p.query ?? '');
    setInvClient(p.client ?? null);
    setInvKind(INVOICE_KINDS.includes(p.kind as InvoiceKind) ? p.kind as InvoiceKind : '');
    setExpCategory(p.category ?? null);
    setUnpaid(p.unpaid === '1');
    setUndated(p.undated === '1');
  };

  // Sequence-guarded fetch: a stale response never lands on top of a newer
  // one (Phase B's list pattern), including after unmount.
  const listSeq = useRef(0);
  const fetchAll = useCallback(() => {
    const seq = ++listSeq.current;
    const fresh = () => seq === listSeq.current;
    const invoicesP = api<{ invoices: InvoiceWithNames[] }>(`/api/admin/finance/invoices${invKind ? `?kind=${invKind}` : ''}`)
      .then((d) => { if (fresh()) setInvoices(d.invoices); })
      .catch(() => {});
    const expensesP = api<{ expenses: ExpenseWithNames[] }>('/api/admin/finance/expenses')
      .then((d) => { if (fresh()) setExpenses(d.expenses); })
      .catch(() => {});
    const reportP = api<FinanceReport>('/api/admin/finance/reports')
      .then((d) => { if (fresh()) setReport(d); })
      .catch(() => {});
    const qualityP = api<QualityReport>('/api/admin/finance/data-quality?worklist=1')
      .then(d => { if (fresh()) { setQualityReport(d); setQualityError(null); } })
      .catch(e => { if (fresh()) setQualityError(e instanceof Error ? e.message : 'Could not load data quality.'); });
    Promise.allSettled([invoicesP, expensesP, reportP, qualityP]).finally(() => { if (fresh()) setLoaded(true); });
  }, [invKind]);
  // The page's own mutations call refresh(), which cancels any debounced
  // `ss:mutated` round first — one change must not cost two refetches.
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchAll();
  }, [fetchAll]);
  useEffect(() => {
    fetchAll();
    // A quick-added invoice or expense has to appear without navigating, so
    // the page listens for the mutation event `lib/api.ts` already fires.
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchAll, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      listSeq.current++;
    };
  }, [fetchAll]);
  useEffect(() => {
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((d) => setClients(d.clients))
      .catch(() => {});
    api<{ projects: ProjectWithClient[] }>('/api/admin/projects')
      .then((d) => setProjects(d.projects))
      .catch(() => {});
  }, []);
  // Saved views drive the filter sheet's chips; ViewChips loads its own copy
  // for the chip row, so this only runs where the sheet exists.
  const loadViews = useCallback(() => {
    api<{ views: SavedViewItem[] }>('/api/admin/views?page=finance')
      .then((d) => setSavedViews(d.views))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!isMobile) return;
    loadViews();
    window.addEventListener('ss:views-changed', loadViews);
    return () => window.removeEventListener('ss:views-changed', loadViews);
  }, [isMobile, loadViews]);

  const mobileState: FinanceFilters = { tab: tab === 'data-quality' ? 'invoices' : tab, query, kind: invKind, client: invClient, category: expCategory, unpaid, undated };
  const applyMobile = (s: FinanceFilters) => {
    setTab(s.tab);
    setQuery(s.query);
    setInvClient(s.client);
    setInvKind(s.kind ?? '');
    setExpCategory(s.category);
    setUnpaid(s.unpaid ?? false);
    setUndated(s.undated ?? false);
  };
  const openFilters = () => {
    setDraft(mobileState);
    setShowFilters(true);
  };
  // The filter footer names what the draft would show, counted the same way
  // each tab filters its own list.
  const [draftInvoices, setDraftInvoices] = useState<InvoiceWithNames[]>([]);
  useEffect(() => {
    if (!showFilters) return;
    let active = true;
    api<{ invoices: InvoiceWithNames[] }>(`/api/admin/finance/invoices${draft.kind ? `?kind=${draft.kind}` : ''}`)
      .then(d => { if (active) setDraftInvoices(d.invoices); }).catch(() => { if (active) setDraftInvoices([]); });
    return () => { active = false; };
  }, [showFilters, draft.kind]);
  const draftCount =
    draft.tab === 'expenses'
      ? expenses.filter(
          (e) =>
            (!draft.category || e.category === draft.category) &&
            matchesQuery(draft.query, e.vendor, e.description, e.project_name, e.category),
        ).length
      : draftInvoices.filter(
          (i) =>
            (!(draft.unpaid || draft.undated) || isUnpaidReceivable(i)) &&
            (!draft.undated || missingAgingDate(i) !== null) &&
            (!draft.client || i.client_id === draft.client) &&
            matchesQuery(draft.query, i.invoice_number, i.client_name, i.project_name, i.notes),
        ).length;

  // Reports: the FY chip and the CSV export both need the selected FY, so it
  // is read here and the export handed down — one implementation, two widths.
  const fy = useSelectedFY(fyKey);
  const fyOptions = report ? fyList(report) : [];
  const fyQuarters = useMemo(
    () => (report?.quarters ?? []).filter((q) => q.key.startsWith(fy)),
    [report, fy],
  );
  const basExport = useCsvExport('bas', { fy }, { count: fyQuarters.length });
  const invoiceExport = useCsvExport('invoices', { view: 'invoices', query, client: invClient, kind: invKind }, {
    count: invoices.filter(i => (!invClient || i.client_id === invClient) && matchesQuery(query, i.invoice_number, i.client_name, i.project_name, i.notes)).length,
    filterLabels: { client: clients.find(c => c.id === invClient)?.name },
  });

  const menuItems: PageMenuItem[] =
    tab === 'data-quality' ? [{ key: 'refresh', label: 'Refresh queue', onSelect: refresh }] :
    tab === 'reports'
      ? [
          { key: 'fy', label: 'Financial year', sublabel: fy, disabled: fyOptions.length === 0, onSelect: () => setFyPicker(true) },
          { key: 'aging-csv', label: 'Export aging CSV', sublabel: 'Receivables by days past due', disabled: agingReady !== agingAsOf, onSelect: () => { window.location.href = agingExportUrl(agingAsOf); } },
          { ...basExport.item, disabled: basExport.busy || fyQuarters.length === 0 },
        ]
      : tab === 'expenses'
        ? [
            { key: 'new', label: 'New expense', onSelect: () => setExpenseModal('new') },
            { key: 'filter', label: 'Filter', onSelect: openFilters },
            { key: 'search', label: 'Search', onSelect: () => window.dispatchEvent(new Event('ss:open-search')) },
          ]
        : [
            { key: 'new', label: 'New invoice', onSelect: () => setShowNewInvoice(true) },
            invoiceExport.item,
            { key: 'select', label: 'Select', sublabel: 'Choose invoices to trash', onSelect: () => setSelectMode(true) },
            { key: 'filter', label: 'Filter', onSelect: openFilters },
            { key: 'search', label: 'Search', onSelect: () => window.dispatchEvent(new Event('ss:open-search')) },
          ];

  return (
    <>
      <PageHeader
        title="Finance"
        selection={isMobile && selection ? selection : undefined}
        mobileActions={
          !isMobile
            ? undefined
            : selection
              ? [{ key: 'done', label: 'Done selecting', icon: <XIcon />, onPress: selection.onDone }]
              : [
                  // Reports has nothing to search or filter — just the menu.
                  ...(tab === 'reports' || tab === 'data-quality'
                    ? []
                    : [
                        {
                          key: 'search',
                          label: 'Search',
                          icon: <SearchIcon />,
                          onPress: () => window.dispatchEvent(new Event('ss:open-search')),
                        },
                        {
                          key: 'filter',
                          label: 'Filter',
                          icon: <FilterIcon />,
                          active: tab === 'invoices' ? invClient !== null || !!invKind || !!query || unpaid || undated : expCategory !== null || !!query,
                          onPress: openFilters,
                        },
                      ]),
                  { key: 'menu', label: 'More finance actions', icon: <DotsIcon />, onPress: () => setShowMenu(true) },
                ]
        }
      />

      {isMobile ? (
        <div className="mtabs finance-tabs" ref={tabsRef}>
          {([
            { value: 'invoices', label: 'Invoices' },
            { value: 'expenses', label: 'Expenses' },
            { value: 'reports', label: 'Reports' },
            { value: 'data-quality', label: `Data quality${qualityReport?.total_issues ? ` · ${qualityReport.total_issues}` : ''}` },
          ] as const).map((t) => (
            <button
              key={t.value}
              type="button"
              className={`t${tab === t.value ? ' on' : ''}`}
              aria-pressed={tab === t.value}
              onClick={() => {
                setTab(t.value);
                setQuery('');
              }}
            >
              {t.label}
            </button>
          ))}
          {/* The FY switcher is a trailing chip on Reports (mockup §8.1 C). */}
          {tab === 'reports' && fyOptions.length > 0 && (
            <button type="button" className="t finance-fy" onClick={() => setFyPicker(true)}>
              {fy} ▾
            </button>
          )}
        </div>
      ) : (
      <div className="toolbar">
        <Tabs
          options={[
            { value: 'invoices', label: 'Invoices' },
            { value: 'expenses', label: 'Expenses' },
            { value: 'reports', label: 'Reports' },
            { value: 'data-quality', label: `Data quality${qualityReport?.total_issues ? ` · ${qualityReport.total_issues}` : ''}` },
          ]}
          value={tab}
          onChange={(t) => {
            setTab(t);
            setQuery('');
          }}
        />
        {tab === 'invoices' && (
          <>
            <FilterBar
              fields={[
                {
                  key: 'client',
                  label: 'Client',
                  items: clients.map((c) => ({ value: c.id, label: c.name })),
                  searchable: true,
                },
              ]}
              active={{ client: invClient }}
              onChange={(_k, v) => setInvClient(v)}
            />
            <SearchBox placeholder="Search number, client, project…" value={query} onChange={setQuery} />
            <CsvExportButton state={invoiceExport} />
            <button className="btn primary" onClick={() => setShowNewInvoice(true)}>
              + New invoice
            </button>
          </>
        )}
        {tab === 'expenses' && (
          <>
            <FilterBar
              fields={[
                {
                  key: 'category',
                  label: 'Category',
                  items: EXPENSE_CATEGORIES.map((c) => ({ value: c, label: capitalize(c) })),
                },
              ]}
              active={{ category: expCategory }}
              onChange={(_k, v) => setExpCategory(v)}
            />
            <SearchBox placeholder="Search vendor, description…" value={query} onChange={setQuery} />
            <button className="btn primary" onClick={() => setExpenseModal('new')}>
              + New expense
            </button>
          </>
        )}
        {tab === 'reports' && report && (
          <FYSwitcher report={report} fyKey={fyKey} />
        )}
      </div>
      )}

      {/* On mobile the save affordance is the filter sheet's "+ Save current",
          so the chip row stays a row of views rather than a lone dashed chip. */}
      {tab !== 'data-quality' && <ViewChips
        page="finance"
        current={viewState}
        isDefault={isDefaultView}
        allowSave={!isMobile}
        leading={!isMobile && tab === 'invoices' && <div className="kind-filter" role="group" aria-label="Invoice kind">
          <span className="lbl">Kind</span>
          {[{ value: '', label: 'All' }, ...invoiceKindOptions].map(({ value, label }) => <button
            key={value} type="button" className={`view-chip${invKind === value ? ' active' : ''}`}
            aria-pressed={invKind === value} onClick={() => setInvKind(value as InvoiceKind | '')}
          >{label}</button>)}
        </div>}
        onApply={applyView}
      />}

      <div className={isMobile ? 'page-body finance-body' : 'page-body'}>
        <div
          className={isMobile ? 'content wide finance-list' : 'content wide'}
          style={isMobile ? undefined : { paddingTop: 4 }}
        >
          {!loaded ? (
            <SkeletonRows />
          ) : (
            <>
              {tab === 'data-quality' && <DataQualityTab report={qualityReport} error={qualityError} clients={clients} onChanged={refresh} />}
              {tab === 'invoices' && (unpaid || undated) && <div className="aging-filter-note">
                <span>{undated ? 'Undated unpaid invoices' : 'All unpaid invoices'}{invClient && ` for ${clients.find(client => client.id === invClient)?.name ?? 'Unknown client'}`}</span>
                <button onClick={() => { setUnpaid(false); setUndated(false); }}>Clear unpaid/date filter ×</button>
              </div>}
              {tab === 'invoices' && (
                <InvoicesTab
                  invoices={filteredInvoices}
                  report={report}
                  query={query}
                  clientFilter={invClient}
                  today={today}
                  currentQuarterKey={currentQuarter.key}
                  fyKey={fyKey}
                  showAllPaid={showAllPaid}
                  onShowAllPaid={() => setShowAllPaid(true)}
                  onOpen={(id) => navigate(`/finance/invoices/${id}`)}
                  onChanged={refresh}
                  modalOpen={showNewInvoice}
                  isMobile={isMobile}
                  selectMode={selectMode}
                  onSelectMode={setSelectMode}
                  onSelection={setSelection}
                />
              )}
              {tab === 'expenses' && (
                <ExpensesTab
                  expenses={expenses}
                  query={query}
                  categoryFilter={expCategory}
                  today={today}
                  currentQuarterKey={currentQuarter.key}
                  showAll={showAllExpenses}
                  onShowAll={() => setShowAllExpenses(true)}
                  onOpen={(exp) => setExpenseModal(exp)}
                  onChanged={refresh}
                  modalOpen={expenseModal !== null}
                  isMobile={isMobile}
                />
              )}
              {tab === 'reports' && (
                <>
                  <ReportsTab
                    report={report}
                    currentQuarterKey={currentQuarter.key}
                    fyKey={fyKey}
                    basExport={basExport}
                  />
                  <ReceivablesAging asOf={agingAsOf} onAsOf={setAgingAsOf} onReady={setAgingReady} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {isMobile && (
        <>
          <PageMenu open={showMenu} title="Finance" onClose={() => setShowMenu(false)} items={menuItems} />
          {showFilters && (
            <FinanceFilterSheet
              draft={draft}
              setDraft={setDraft}
              clients={clients}
              views={savedViews}
              count={draftCount}
              onClose={() => setShowFilters(false)}
              onApply={() => {
                applyMobile(draft);
                setShowFilters(false);
              }}
              onSave={(name) => {
                void api('/api/admin/views', {
                  method: 'POST',
                  json: { page: 'finance', name, params: financeSavedParams(draft) },
                })
                  .catch((error) => reportError(error, 'Could not save the view.'));
              }}
            />
          )}
          {fyPicker && (
            <PickerSheet
              open
              title="Financial year"
              items={fyOptions.map((k) => ({ value: k, label: k }))}
              value={fy}
              onSelect={setSelectedFY}
              onClose={() => setFyPicker(false)}
            />
          )}
        </>
      )}

      {showNewInvoice && (
        <NewInvoiceModal
          onClose={() => setShowNewInvoice(false)}
          onCreated={refresh}
        />
      )}
      {expenseModal && (
        <ExpenseModal
          initial={expenseModal === 'new' ? null : expenseModal}
          projects={projects}
          onClose={() => setExpenseModal(null)}
          onSaved={refresh}
        />
      )}
    </>
  );
}
